import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConversationWithDetails, Message } from '@/services/messageService';
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar';

interface ConversationListProps {
  activeConversationId?: string;
  onSelectConversation?: (conversationId: string) => void;
  onNewConversation?: () => void;
}

type ExtendedMessage = Message & { 
  parent?: { first_name: string; last_name: string };
  organization?: { name: string };
};

const ConversationList: React.FC<ConversationListProps> = ({ 
  activeConversationId,
  onSelectConversation,
  onNewConversation 
}) => {
  const { user } = useAuth();
  const { conversationId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();
  
  // Use either the passed activeConversationId or the URL param
  const currentConversationId = activeConversationId || conversationId;

  // Fetch conversations with related family info and message counts
  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Fetch conversations with their most recent message timestamp
      const { data: conversationsData, error } = await supabase
        .from('conversations')
        .select(`
          *,
          family:family_id (id, name)
        `);
        // Note: We'll sort after getting actual last message times

      if (error) {
        console.error("Error fetching conversations:", error);
        throw error;
      }

      // For each conversation, get the last message and unread count
      const conversationPromises = conversationsData.map(async (conv) => {
        try {
          // Get the last message for this conversation
          const { data: lastMessages, error: msgError } = await supabase
            .from('messages')
            .select(`
              id,
              conversation_id,
              sender_id,
              sender_type,
              content,
              created_at,
              updated_at,
              organization_id
            `)
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (msgError) throw msgError;
          
          // Get sender information for the last message if needed
          let senderInfo = null;
          if (lastMessages && lastMessages.length > 0) {
            if (lastMessages[0].sender_type === 'parent') {
              const { data: parentData } = await supabase
                .from('parents')
                .select('first_name, last_name')
                .eq('id', lastMessages[0].sender_id)
                .single();
                
              senderInfo = parentData;
            } else if (lastMessages[0].sender_type === 'staff') {
              // For staff messages, get organization name
              const { data: orgData } = await supabase
                .from('organizations')
                .select('name')
                .eq('id', lastMessages[0].organization_id)
                .single();
                
              if (orgData) {
                senderInfo = { name: orgData.name };
              }
            }
          }

          // Fix: Get unread count by making a separate query
          // This avoids the SQL syntax error with the previous approach
          let unreadCount = 0;
          if (user?.id) {
            // Step 1: Get all messages in this conversation not sent by current user
            const { data: messagesData } = await supabase
              .from('messages')
              .select('id')
              .eq('conversation_id', conv.id)
              .neq('sender_id', user.id);

            if (messagesData && messagesData.length > 0) {
              const messageIds = messagesData.map(m => m.id);
              
              // Step 2: Get all read messages by this user
              const { data: readMessages } = await supabase
                .from('message_reads')
                .select('message_id')
                .eq('user_id', user.id)
                .in('message_id', messageIds);

              // Step 3: Calculate unread count
              const readMessageIds = new Set(readMessages?.map(r => r.message_id) || []);
              unreadCount = messageIds.filter(id => !readMessageIds.has(id)).length;
            }
          }
          
          return {
            ...conv,
            last_message: lastMessages && lastMessages.length > 0 ?
              {
                ...lastMessages[0],
                parent: lastMessages[0].sender_type === 'parent' ? senderInfo : null,
                organization: lastMessages[0].sender_type === 'staff' ? senderInfo : null
              } as ExtendedMessage
              : null,
            unread_count: unreadCount,
            family: conv.family || { id: '', name: '' }
          } as ConversationWithDetails;
        } catch (error) {
          console.error(`Error processing conversation ${conv.id}:`, error);
          return {
            ...conv,
            last_message: null,
            unread_count: 0,
            family: conv.family || { id: '', name: '' }
          } as ConversationWithDetails;
        }
      });
      
      const conversationsWithDetails = await Promise.all(conversationPromises);
      
      // Sort conversations by actual last message time (most recent first)
      return conversationsWithDetails.sort((a, b) => {
        const timeA = a.last_message?.created_at || a.created_at;
        const timeB = b.last_message?.created_at || b.created_at;
        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });
    },
    enabled: !!user,
    staleTime: 10000, // 10 seconds
  });

  // Set up polling to refresh conversations every 60 seconds instead of a global real-time subscription to messages
  useEffect(() => {
    if (!user) return;
    // Immediately fetch conversations
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    // Poll every 60 seconds
    const intervalId = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }, 60000);
    return () => clearInterval(intervalId);
  }, [user, queryClient]);

  // Set up realtime subscription to conversations table only
  useEffect(() => {
    if (!user) return;
    const conversationsChannel = supabase
      .channel('conversations-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'conversations' }, 
        () => {
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(conversationsChannel);
    };
  }, [user, queryClient]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const familyName = conversation.family?.name?.toLowerCase() || '';
    const lastMessage = conversation.last_message?.content?.toLowerCase() || '';
    
    return familyName.includes(query) || lastMessage.includes(query);
  });

  const handleConversationClick = (conversationId: string) => {
    if (onSelectConversation) {
      onSelectConversation(conversationId);
    }
  };

  // Helper to format last message time - recalculate each time to avoid sticky dates  
  const getLastMessageTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    if (isToday(date)) return format(date, 'h:mm a');
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d, yyyy');
  };

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader>
        <CardTitle>Conversations</CardTitle>
        <CardDescription>
          With families in your hive
        </CardDescription>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search conversations..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto p-0">
        {isLoading ? (
          <div className="p-4 text-center text-muted-foreground">Loading conversations...</div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground gap-4">
            <Users className="h-12 w-12 text-muted-foreground/30" />
            <div>
              <div className="font-semibold text-lg mb-1">No conversations</div>
              <div className="text-sm">{searchQuery ? 'No conversations match your search.' : 'Start a new message to connect with families!'}</div>
            </div>
          </div>
        ) : (
          <div className="divide-y">
            {filteredConversations.map((conversation) => {
              const isSelected = currentConversationId === conversation.id;
              const isUnread = conversation.unread_count > 0;
              return (
                <div
                  key={conversation.id}
                  className={`group flex cursor-pointer items-center gap-3 px-4 py-3 transition-all rounded-lg mx-2 my-1
                    ${isSelected ? 'bg-primary/10 border border-primary shadow-sm' : 'hover:bg-muted'}
                  `}
                  aria-label={`Conversation with ${conversation.family?.name || 'Family'}`}
                  tabIndex={0}
                  onClick={() => handleConversationClick(conversation.id)}
                  onKeyDown={e => { if (e.key === 'Enter') handleConversationClick(conversation.id); }}
                >
                  <EnhancedAvatar
                    userName={conversation.family?.name || 'Family'}
                    size="md"
                    className="flex-shrink-0 shadow-sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-base ${isUnread ? 'font-bold text-primary' : 'font-medium'}`}>{conversation.family?.name || 'Family'}</span>
                      <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
                        {getLastMessageTime(conversation.last_message?.created_at || conversation.created_at)}
                      </span>
                    </div>
                    {conversation.last_message && (
                      <div className="flex items-center gap-1">
                        <span className="truncate text-sm text-muted-foreground/80">
                          {conversation.last_message.sender_type === 'parent' &&
                            (conversation.last_message as ExtendedMessage).parent && (
                              <span className="font-medium">
                                {(conversation.last_message as ExtendedMessage).parent!.first_name}: {' '}
                              </span>
                            )}
                          {conversation.last_message.sender_type === 'staff' &&
                            (conversation.last_message as ExtendedMessage).organization && (
                              <span className="font-medium">
                                {(conversation.last_message as ExtendedMessage).organization!.name}: {' '}
                              </span>
                            )}
                          {conversation.last_message.content}
                        </span>
                      </div>
                    )}
                  </div>
                  {isUnread && (
                    <span className="ml-2 flex h-3 w-3 items-center justify-center rounded-full bg-primary animate-pulse" aria-label="Unread messages"></span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationList;
